import { useRef } from 'react';

import { AGENTS, AgentMessage, UserMessage } from '@/components/rooms';
import type { BrandGuideResponse, GetCompetitorsResponse, SessionSnapshot } from '@/contracts/onboarding-session';

import { researchPending, type CardSpec, type DataState, type TranscriptItem } from '../state/buildTranscript';
import { BrandGuideCard } from './BrandGuideCard';
import { BuildCard } from './BuildCard';
import { CompetitorsResearchCard } from './CompetitorsResearchCard';
import { ResearchCard } from './ResearchCard';

export interface TranscriptViewProps {
  items: readonly TranscriptItem[];
  snapshot: SessionSnapshot | null;
  brandGuide: { data: BrandGuideResponse | null; loading: boolean; failed: boolean };
  /** The site as typed, for the cards' titles before the first snapshot. */
  site?: string;
  /** Renders the ad feed card of a platform (owns that platform's polling hook). */
  renderAds: (platform: Extract<CardSpec, { kind: 'ads' }>['platform']) => React.ReactNode;
  /** GET /competitors, for the competitors research card's steps. */
  competitors?: DataState<GetCompetitorsResponse>;
  /** The inline "Finish onboarding" card was pressed. */
  onBuild: () => void;
  /** Passed to the Build card: clicks are ignored this long after it mounts. Tests turn it off with 0. */
  clickGuardMs?: number;
}

interface CardViewProps extends Omit<TranscriptViewProps, 'items'> {
  card: CardSpec;
  /** The card arrived while the person was here (not part of a resumed chat): it may take focus when it is the next step. */
  arrived: boolean;
}

function CardView({ card, arrived, snapshot, brandGuide, renderAds, site, competitors, onBuild, clickGuardMs }: CardViewProps) {
  switch (card.kind) {
    case 'research':
      return <ResearchCard snapshot={snapshot} brandGuide={brandGuide.data} site={site} />;
    case 'brandGuide':
      return <BrandGuideCard
          guide={brandGuide.data}
          loading={brandGuide.loading}
          failed={brandGuide.failed}
          site={snapshot?.domain || site}
          researchPending={researchPending(snapshot)}
        />;
    case 'ads':
      return <>{renderAds(card.platform)}</>;
    case 'competitorsResearch':
      return <CompetitorsResearchCard brand={card.brand} snapshot={snapshot} competitors={competitors} />;
    case 'build':
      return <BuildCard brand={card.brand} ready={card.ready} onBuild={onBuild} clickGuardMs={clickGuardMs} focusOnArrival={arrived} />;
  }
}

/** Draws the derived transcript: pure mapping from items to the rooms messages and the cards. */
export function TranscriptView({ items, snapshot, brandGuide, renderAds, site, competitors, onBuild, clickGuardMs }: TranscriptViewProps) {
  // The lines already there when the view first drew (a resumed session) appear at once; later ones rise in.
  const initialIds = useRef<ReadonlySet<string> | null>(null);
  if (initialIds.current === null) initialIds.current = new Set(items.map((item) => item.id));
  const isNew = (id: string) => !initialIds.current?.has(id);
  return (
    <>
      {items.map((item) => {
        switch (item.kind) {
          case 'user':
            return (
              <UserMessage key={item.id} animate={isNew(item.id)}>
                {item.text}
              </UserMessage>
            );
          case 'agent':
            return (
              <AgentMessage
                key={item.id}
                agent={AGENTS[item.agent]}
                lines={item.lines}
                animate={isNew(item.id)}
                // The quiz reply is one line replaced on every answer: each new text fades in.
                fadeLines={item.id === 'ack'}
              >
                {item.card ? (
                  <CardView
                    card={item.card}
                    arrived={isNew(item.id)}
                    snapshot={snapshot}
                    brandGuide={brandGuide}
                    renderAds={renderAds}
                    site={site}
                    competitors={competitors}
                    onBuild={onBuild}
                    clickGuardMs={clickGuardMs}
                  />
                ) : null}
              </AgentMessage>
            );
        }
      })}
    </>
  );
}

